import { Component, HostListener, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';

interface GalleryPhoto {
  event_title?: string;
  event_description?: string;
  event_image: string;
  [key: string]: any;
}

interface GalleryDataMap {
  [year: string]: GalleryPhoto[];
}

interface GalleryGroup {
  event_title: string;
  event_description: string;
  photos: GalleryPhoto[];
}

@Component({
  selector: 'app-gallery-page',
  templateUrl: './gallery-page.component.html',
  styleUrls: ['./gallery-page.component.scss']
})
export class GalleryPageComponent implements OnInit {
  marqueeList: string[] = [];

  readonly apiUrlMarquee = 'https://fmcet.ac.in/backend/getMarquee.php';
  readonly apiUrlGallery = 'https://fmcet.ac.in/backend/getGallery.php';
  readonly imageBaseUrl = 'https://fmcet.ac.in/websiteupdate/uploads/';

  galleryData: GalleryDataMap = {};
  availableYears: string[] = [];
  selectedTab = '';
  galleryList: GalleryPhoto[] = [];
  groupedPhotos: GalleryGroup[] = [];
  fullscreenImage: string | null = null;

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    this.loadMarqueeData();
    this.loadGalleryData();
  }

  selectTab(tab: string): void {
    this.selectedTab = tab;
    this.updateGalleryList();
  }

  loadMarqueeData(): void {
    this.http.get<string[]>(this.apiUrlMarquee).subscribe({
      next: (data) => {
        this.marqueeList = Array.isArray(data) && data.length
          ? data
          : ['No marquee content available'];
      },
      error: (error) => {
        console.error('Error loading marquee:', error);
        this.marqueeList = ['Unable to load highlights at the moment.'];
      }
    });
  }

  loadGalleryData(): void {
    this.http.get<GalleryDataMap>(this.apiUrlGallery).subscribe({
      next: (data) => {
        this.galleryData = data || {};

        this.availableYears = Object.keys(this.galleryData).sort((a, b) => {
          const yearA = Number(a);
          const yearB = Number(b);

          if (!Number.isNaN(yearA) && !Number.isNaN(yearB)) {
            return yearB - yearA;
          }

          return b.localeCompare(a);
        });

        this.selectedTab = this.availableYears.length ? this.availableYears[0] : '';
        this.updateGalleryList();
      },
      error: (error) => {
        console.error('Error loading gallery:', error);
        this.galleryData = {};
        this.availableYears = [];
        this.galleryList = [];
        this.groupedPhotos = [];
      }
    });
  }

  updateGalleryList(): void {
    this.galleryList = this.selectedTab && this.galleryData[this.selectedTab]
      ? this.galleryData[this.selectedTab]
      : [];

    this.groupPhotosByEventTitle();
  }

  groupPhotosByEventTitle(): void {
    const grouped: Record<string, GalleryGroup> = {};

    this.galleryList.forEach((photo: GalleryPhoto) => {
      const title = (photo.event_title || 'Untitled Event').trim();
      const description = (photo.event_description || '').trim();

      if (!grouped[title]) {
        grouped[title] = {
          event_title: title,
          event_description: description,
          photos: []
        };
      }

      grouped[title].photos.push(photo);
    });

    this.groupedPhotos = Object.values(grouped);
  }

  getImageUrl(path: string): string {
    if (!path) {
      return '';
    }

    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    return `${this.imageBaseUrl}${path}`;
  }

  openFullscreen(imageSrc: string): void {
    this.fullscreenImage = imageSrc;
    document.body.style.overflow = 'hidden';
  }

  closeFullscreen(): void {
    this.fullscreenImage = null;
    document.body.style.overflow = '';
  }

  trackByPhoto(index: number, photo: GalleryPhoto): string {
    return `${photo.event_image}-${index}`;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.fullscreenImage) {
      this.closeFullscreen();
    }
  }
}